/** Gameplay ECS components for Sprint 2 (workers & movement). Plain data records
 * declared through the ECS `defineComponent` factory. */
import { defineComponent } from '../ecs/component.js';

/** Continuous position in tile coordinates (fractional). Tile (t) centre is at
 * integer (t.x, t.y); the renderer projects this to isometric screen space. */
export interface Position {
  x: number;
  y: number;
}

/** Follows a precomputed tile path. `index` is the waypoint the unit currently
 * sits on; it advances toward `path[index + 1]`. */
export interface PathFollower {
  /** Tile indices from start to goal (inclusive). */
  path: number[];
  index: number;
  arrived: boolean;
}

export interface Movement {
  /** Tiles per second at cost-1 terrain. */
  speed: number;
}

export enum WorkerState {
  Idle = 'idle',
  MovingToWork = 'moving_to_work',
  Working = 'working',
  Returning = 'returning',
}

export interface Worker {
  state: WorkerState;
  homeTile: number;
  workTile: number;
  /** Remaining work ticks in the Working state. */
  workTicksRemaining: number;
  /** Number of completed home→work→home cycles (Sprint 3 will make this haul goods). */
  trips: number;
}

/** Previous-tick position, kept so the renderer can interpolate between the
 * 20 TPS simulation and 60 FPS rendering for smooth motion. */
export interface RenderInterp {
  prevX: number;
  prevY: number;
}

export const CPosition = defineComponent<Position>('Position', () => ({ x: 0, y: 0 }));
export const CPathFollower = defineComponent<PathFollower>('PathFollower', () => ({
  path: [],
  index: 0,
  arrived: true,
}));
export const CMovement = defineComponent<Movement>('Movement', () => ({ speed: 2 }));
export const CWorker = defineComponent<Worker>('Worker', () => ({
  state: WorkerState.Idle,
  homeTile: 0,
  workTile: 0,
  workTicksRemaining: 0,
  trips: 0,
}));
export const CRenderInterp = defineComponent<RenderInterp>('RenderInterp', () => ({
  prevX: 0,
  prevY: 0,
}));
